import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../../shared/lib/firebase';
import type { UserDoc } from '../../shared/types/users';
import type { RoleDoc } from '../../shared/types/roles';
import { claimSession, releaseSession } from '../../shared/lib/callables';
import { AuthContext, type AuthContextValue, type AuthStatus, type SessionState } from './AuthContext';
import { detectSlot, isDisplaced } from './session';

type ClaimState = {
  /** Preso ao objeto User: novo login (mesmo uid) nunca herda o claim anterior. */
  user: FirebaseUser;
  phase: 'checking' | 'ok' | 'conflict' | 'error';
  authTime: number | null;
  /** claimedAt (ms) devolvido pelo servidor no ok. */
  claimedAt: number;
  /** reclaim/force em voo: não deriva derrubada. */
  busy: boolean;
  exempt: boolean;
  since: number;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [userDoc, setUserDoc] = useState<UserDoc | null>(null);
  const [role, setRole] = useState<RoleDoc | null>(null);
  const [userDocLoaded, setUserDocLoaded] = useState(false);
  const [claim, setClaim] = useState<ClaimState | null>(null);
  const slot = useMemo(() => detectSlot(navigator), []);

  // Effect 1: track auth state; resets derived state on sign-out
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        setCurrentUser(user);
        setStatus(user ? 'signed-in' : 'signed-out');
        if (!user) {
          setUserDoc(null);
          setRole(null);
          setUserDocLoaded(false);
        }
      },
      (error) => {
        console.error('[auth] onAuthStateChanged error', error);
        setCurrentUser(null);
        setStatus('signed-out');
      }
    );
    return unsubscribe;
  }, []);

  // Effect 2: subscribe to users/{uid} while a user is signed in
  useEffect(() => {
    if (!currentUser) return;

    const unsub = onSnapshot(
      doc(db, `users/${currentUser.uid}`),
      (snap) => {
        const next = snap.exists() ? { ...(snap.data() as UserDoc), uid: snap.id } : null;
        setUserDoc(next);
        setUserDocLoaded(true);
      },
      (error) => {
        console.error('[auth] users/{uid} snapshot error', error);
        setUserDoc(null);
        setUserDocLoaded(true);
      }
    );
    return unsub;
  }, [currentUser]);

  // Effect 3: subscribe to roles/{roleId} when userDoc provides a roleId
  useEffect(() => {
    if (!userDoc?.roleId) return;

    const unsub = onSnapshot(
      doc(db, `roles/${userDoc.roleId}`),
      (snap) => {
        setRole(snap.exists() ? { ...(snap.data() as RoleDoc), id: snap.id } : null);
      },
      (error) => {
        console.error('[auth] roles/{id} snapshot error', error);
        setRole(null);
      }
    );
    return unsub;
  }, [userDoc?.roleId]);

  // P92: reivindica a vaga do aparelho. Puro: devolve o estado, quem chama aplica.
  const runClaim = useCallback(
    async (user: FirebaseUser, force: boolean): Promise<ClaimState> => {
      const base = { user, authTime: null as number | null, claimedAt: 0, busy: false, exempt: false, since: 0 };
      try {
        const authTime = Number((await user.getIdTokenResult()).claims.auth_time);
        const res = await claimSession({
          slot,
          force,
          userAgent: navigator.userAgent.slice(0, 300),
        });
        const r = res.data;
        if (r.status === 'conflict') {
          return { ...base, authTime, phase: 'conflict', since: r.since };
        }
        return {
          ...base, authTime, phase: 'ok', exempt: r.status === 'exempt',
          claimedAt: r.status === 'ok' ? r.claimedAt : 0,
        };
      } catch (error) {
        console.error('[auth] claimSession error', error);
        return { ...base, phase: 'error' };
      }
    },
    [slot]
  );

  // Effect 4: ao entrar, reivindica a vaga. Sem setState síncrono aqui (feedback_react_hooks_set_state_in_effect).
  useEffect(() => {
    if (!currentUser) return;
    let off = false;
    // 'checking' inicial é derivado (claim ainda nulo / de outro uid)
    void runClaim(currentUser, false).then((c) => { if (!off) setClaim(c); });
    return () => { off = true; };
  }, [currentUser, runClaim]);

  const resolveSessionConflict = useCallback(
    async (takeOver: boolean): Promise<void> => {
      if (!currentUser) return;
      if (takeOver) {
        setClaim(null);
        setClaim(await runClaim(currentUser, true));
      } else {
        await fbSignOut(auth);
      }
    },
    [currentUser, runClaim]
  );
  const retrySession = useCallback(async (): Promise<void> => {
    if (!currentUser) return;
    setClaim(null);
    setClaim(await runClaim(currentUser, false));
  }, [currentUser, runClaim]);
  const reclaimSession = useCallback(async (): Promise<void> => {
    if (!currentUser) return;
    setClaim((c) => (c ? { ...c, busy: true } : c)); // tela fica montada durante o reclaim
    setClaim(await runClaim(currentUser, true));
  }, [currentUser, runClaim]);

  // Derivado no render: claim de outro User (troca de conta ou relogin) conta como 'checking'.
  const current = currentUser && claim?.user === currentUser ? claim : null;
  let session: SessionState = 'checking';
  if (current) {
    session = current.phase;
    if (
      current.phase === 'ok' && !current.exempt && !current.busy && userDoc && current.authTime !== null &&
      isDisplaced(userDoc.sessions, slot, { authTime: current.authTime, claimedAt: current.claimedAt })
    ) {
      session = 'displaced';
    }
  }
  const conflictSince = current?.phase === 'conflict' ? current.since : null;
  const sessionConflict = useMemo(
    () => (conflictSince === null ? null : { slot, since: conflictSince }),
    [slot, conflictSince]
  );

  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    await signInWithEmailAndPassword(auth, email.trim(), password);
  }, []);

  const holdsSlot = session === 'ok' && !current?.exempt;
  const signOut = useCallback(async (): Promise<void> => {
    if (holdsSlot) {
      try {
        await releaseSession({ slot });
      } catch {
        // best-effort: a vaga também se libera no próximo login forçado
      }
    }
    await fbSignOut(auth);
  }, [holdsSlot, slot]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status, currentUser, userDoc, role, userDocLoaded, signIn, signOut,
      session, sessionConflict, resolveSessionConflict, retrySession, reclaimSession,
    }),
    [status, currentUser, userDoc, role, userDocLoaded, signIn, signOut,
      session, sessionConflict, resolveSessionConflict, retrySession, reclaimSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
